import { Colors, Icon } from '@amelio/ui-library';

import { HighlightCard } from './HighlightCard';
import { formatAverage } from '../utils/levelScale';
import { selectMatrixHighlights } from '../utils/matrixHighlights';

import type { CompetencyMatrixGroup, CompetencyMatrixReport } from '@/types/reports';

import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

interface MatrixHighlightsProps {
    report: CompetencyMatrixReport;
    language: string;
}

/** The three summary cards above the matrix: how many employees are shown, the category most in
 * need of improvement (lowest average) and the strongest one (highest average). */
export const MatrixHighlights = ({ report, language }: MatrixHighlightsProps) => {
    const { t } = useAmelioTranslations();
    const { weakestGroup, strongestGroup } = selectMatrixHighlights(report);
    const scale = report.levels.length;
    const displayedCount = report.employees.length;

    const groupValue = (group: CompetencyMatrixGroup | null): string =>
        group ? getLocalizedText(group.name) : t('reports.competencyMatrix.highlights.noData');

    const groupDescription = (group: CompetencyMatrixGroup | null): string | undefined =>
        group && group.average !== null
            ? formatAverage({ average: group.average, scale, language })
            : undefined;

    return (
        <div className={'grid grid-cols-1 gap-4 sm:grid-cols-3'}>
            <HighlightCard
                icon={
                    <Icon
                        name={'users-people'}
                        size={'lg'}
                        color={Colors.PRIMARY}
                    />
                }
                value={displayedCount}
                // The card already shows the count as its value; repeating it in the label would
                // read as "13 / 13 employees".
                label={t('reports.competencyMatrix.highlights.employees')}
                description={
                    report.isTruncated
                        ? t('reports.competencyMatrix.truncated', { count: report.employeeCount })
                        : t('reports.competencyMatrix.highlights.employeesDescription')
                }
                dataTestId={'matrix-highlight-employees'}
            />

            <HighlightCard
                icon={
                    <Icon
                        name={'business-trend-down'}
                        size={'lg'}
                        color={Colors.PRIMARY}
                    />
                }
                value={groupValue(weakestGroup)}
                label={t('reports.competencyMatrix.highlights.toImprove')}
                description={groupDescription(weakestGroup)}
                dataTestId={'matrix-highlight-to-improve'}
            />

            <HighlightCard
                icon={
                    <Icon
                        name={'business-trend-up'}
                        size={'lg'}
                        color={Colors.PRIMARY}
                    />
                }
                value={groupValue(strongestGroup)}
                label={t('reports.competencyMatrix.highlights.strongest')}
                description={groupDescription(strongestGroup)}
                dataTestId={'matrix-highlight-strongest'}
            />
        </div>
    );
};
